"use client";

import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { css, cx } from "styled-system/css";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { useAuth } from "@/providers/AuthProvider";
import { Inbox, Home, CalendarDays, Package, Settings2, Trophy, ChevronDown, ShoppingBag, Stethoscope, ClipboardList, Wallet } from "lucide-react";

/** Rutas que se gestionan desde el submenú de Configuración (SettingsLayout). */
function isConfigArea(pathname: string): boolean {
  if (pathname === "/dashboard/settings" || pathname.startsWith("/dashboard/settings/")) return true;
  const roots = [
    "/dashboard/schedule",
    "/dashboard/payments",
    "/dashboard/notifications",
    "/dashboard/appointments",
    "/dashboard/recurring",
    "/dashboard/clients",
    "/dashboard/subscription",
    "/dashboard/team",
    "/dashboard/agendas",
    "/dashboard/requests",
  ];
  return roots.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

const CONFIG_HREF = "/dashboard/settings";

const mainNav = [
  { href: "/dashboard", label: "Panel" },
  { href: "/dashboard/calendar", label: "Calendario" },
  { href: "/dashboard/services", label: "Servicios" },
  { href: "/dashboard/programs", label: "Programas" },
];

function isNavActive(pathname: string, href: string) {
  if (href === "/dashboard") return pathname === "/dashboard";
  return pathname === href || pathname.startsWith(`${href}/`);
}

type AgendaItem = { id: string; name: string; slug: string; isPrimary: boolean; color?: string | null };

function AgendaSwitcher() {
  const { refresh } = useAuth();
  const [agendas, setAgendas] = useState<AgendaItem[]>([]);
  const [activePid, setActivePid] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const loadAgendas = async () => {
    const r = await fetchWithSessionSync("/api/dashboard/agendas", { credentials: "include" }, refresh);
    if (!r.ok) return;
    const data = (await r.json()) as { agendas: AgendaItem[]; activeProfessionalId: string | null };
    setAgendas(data.agendas);
    setActivePid(data.activeProfessionalId);
  };

  useEffect(() => {
    void loadAgendas();
  }, [refresh]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const active = agendas.find((a) => a.id === activePid) ?? agendas[0];

  async function switchTo(id: string) {
    if (id === activePid) { setOpen(false); return; }
    setSwitching(true);
    setOpen(false);
    await fetchWithSessionSync(`/api/dashboard/agendas/${id}/switch`, { method: "POST", credentials: "include" }, refresh);
    window.location.reload();
  }

  return (
    <div ref={ref} className={css({ position: "relative", mb: "3" })}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={switching}
        className={css({
          w: "full",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "2",
          px: "3",
          py: "2",
          rounded: "lg",
          borderWidth: "1px",
          borderColor: "rgba(255,255,255,0.22)",
          bg: "rgba(255,255,255,0.08)",
          color: "white",
          fontSize: "sm",
          fontWeight: "semibold",
          cursor: switching ? "wait" : "pointer",
          transition: "opacity 120ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
          _hover: { opacity: 0.82 },
          _active: { transform: "scale(0.97)" },
        })}
      >
        <span className={css({ display: "flex", alignItems: "center", gap: "1.5", minW: 0 })}>
          {active?.color && !switching ? (
            <span
              className={css({ flexShrink: 0, w: "2.5", h: "2.5", rounded: "full" })}
              style={{ backgroundColor: active.color }}
            />
          ) : null}
          <span className={css({ fontSize: "xs", opacity: 0.6, flexShrink: 0 })}>Agenda:</span>
          <span className={css({ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" })}>
            {switching ? "Cambiando…" : (active?.name ?? "—")}
          </span>
        </span>
        <ChevronDown
          size={12}
          aria-hidden
          className={css({ flexShrink: 0, transition: "transform 200ms cubic-bezier(0.23, 1, 0.32, 1)", opacity: 0.65 })}
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </button>

      {open && (
        <div
          className={css({
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 100,
            bg: "white",
            rounded: "lg",
            borderWidth: "1px",
            borderColor: "#e2e8f0",
            shadow: "lg",
            overflow: "hidden",
          })}
        >
          {agendas.length > 0 && (
            <>
              <div className={css({ px: "3", py: "1.5", fontSize: "xs", color: "#94a3b8", fontWeight: "semibold", bg: "#f8fafc", borderBottomWidth: "1px", borderColor: "#e2e8f0" })}>
                Mis agendas
              </div>
              {agendas.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => void switchTo(a.id)}
                  className={css({
                    display: "flex",
                    alignItems: "center",
                    gap: "2",
                    w: "full",
                    textAlign: "left",
                    px: "4",
                    py: "3",
                    fontSize: "sm",
                    color: a.id === activePid ? "token(colors.brand.700)" : "#0f172a",
                    bg: a.id === activePid ? "rgba(99,102,241,0.07)" : "white",
                    fontWeight: a.id === activePid ? "semibold" : "normal",
                    cursor: "pointer",
                    borderBottomWidth: "1px",
                    borderColor: "#f1f5f9",
                    _hover: { bg: a.id === activePid ? "rgba(99,102,241,0.1)" : "#f8fafc" },
                    _last: { borderBottom: "none" },
                  })}
                >
                  <span
                    className={css({
                      flexShrink: 0,
                      w: "3",
                      h: "3",
                      rounded: "full",
                      borderWidth: "2px",
                      borderColor: "white",
                      boxShadow: "0 0 0 1.5px rgba(0,0,0,0.12)",
                    })}
                    style={{ backgroundColor: a.color ?? "#6366f1" }}
                  />
                  <span className={css({ minW: 0, flex: 1 })}>
                    <span className={css({ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" })}>
                      {a.name}
                    </span>
                    <span className={css({ display: "block", fontSize: "xs", color: "#94a3b8", mt: "0.5" })}>
                      {a.isPrimary ? "Principal · " : ""}/book/{a.slug}
                    </span>
                  </span>
                </button>
              ))}
            </>
          )}

          <div className={css({ borderTopWidth: agendas.length > 0 ? "1px" : "0", borderColor: "#e2e8f0" })}>
            <Link
              to="/dashboard/agendas?new=1"
              onClick={() => setOpen(false)}
              className={css({
                display: "flex",
                alignItems: "center",
                gap: "2",
                px: "4",
                py: "3",
                fontSize: "sm",
                color: "token(colors.brand.700)",
                bg: "white",
                textDecoration: "none",
                fontWeight: "semibold",
                _hover: { bg: "rgba(99,102,241,0.06)" },
              })}
            >
              <span className={css({
                w: "5",
                h: "5",
                rounded: "full",
                bg: "token(colors.brand.600)",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "md",
                lineHeight: 1,
                flexShrink: 0,
              })}>+</span>
              Crear nueva agenda
            </Link>
            {agendas.length > 1 && (
              <Link
                to="/dashboard/agendas"
                onClick={() => setOpen(false)}
                className={css({
                  display: "block",
                  px: "4",
                  py: "2.5",
                  fontSize: "xs",
                  color: "#64748b",
                  bg: "#f8fafc",
                  textDecoration: "none",
                  _hover: { bg: "#f1f5f9" },
                })}
              >
                Gestionar agendas →
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Solo enlaces de sección; marca y cuenta van en `DashboardLayout`. */
export function DashboardNav() {
  const { refresh } = useAuth();
  const { pathname } = useLocation();
  const [pendingHref, setPendingHref] = useState<string | null>(null);
  const [requestsCount, setRequestsCount] = useState(0);
  const [accountType, setAccountType] = useState<"PROFESSIONAL" | "FACILITY">("PROFESSIONAL");
  const [hasMarketFeatures, setHasMarketFeatures] = useState(false);
  const [hasOdontogram, setHasOdontogram] = useState(false);
  const [hasProPlan, setHasProPlan] = useState(false);

  useEffect(() => {
    setPendingHref(null);
  }, [pathname]);

  useEffect(() => {
    void fetchWithSessionSync("/api/dashboard/appointment-requests", { credentials: "include" }, refresh)
      .then(async (r) => {
        if (!r.ok) return;
        const data = (await r.json()) as { requests: unknown[] };
        setRequestsCount(data.requests.length);
      })
      .catch(() => {});
  }, [refresh]);

  useEffect(() => {
    void fetchWithSessionSync("/api/dashboard/me", { credentials: "include" }, refresh)
      .then(async (r) => {
        if (!r.ok) return;
        const data = (await r.json()) as { accountType?: string; professional?: { plan?: string } };
        if (data.accountType === "FACILITY") setAccountType("FACILITY");
        if (data.professional?.plan === "PRO" || data.professional?.plan === "ENTERPRISE") setHasProPlan(true);
      })
      .catch(() => {});
  }, [refresh]);

  useEffect(() => {
    void fetchWithSessionSync("/api/market/features", { credentials: "include" }, refresh)
      .then(async (r) => {
        if (!r.ok) return;
        const data = (await r.json()) as {
          features: Array<{ slug: string; subscription: { status: string | null } | null }>;
        };
        const active = data.features.filter(
          (f) => f.subscription?.status === "ACTIVE" || f.subscription?.status === "TRIAL",
        );
        setHasMarketFeatures(active.length > 0);
        setHasOdontogram(
          active.some((f) => f.slug === "odontogram"),
        );
      })
      .catch(() => {});
  }, [refresh]);

  const baseLinkClass = css({
    fontSize: "sm",
    py: "2",
    px: "3",
    rounded: "md",
    color: "rgba(255,255,255,0.65)",
    display: "flex",
    alignItems: "center",
    gap: "2.5",
    textDecoration: "none",
    transition: "opacity 120ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
    _hover: { opacity: 1, color: "white" },
    _active: { transform: "scale(0.97)" },
  });

  const activeLinkClass = css({
    bg: "rgba(255,255,255,0.1)",
    color: "white",
    fontWeight: "semibold",
    boxShadow: "inset 2px 0 0 rgba(255,255,255,0.6)",
    opacity: 1,
  });

  function NavIcon({ href, isFacility }: { href: string; isFacility: boolean }) {
    const p = { size: 14, strokeWidth: 1.75, "aria-hidden": true };
    if (href === "/dashboard") return <Home {...p} />;
    if (href.includes("calendar")) return <CalendarDays {...p} />;
    if (isFacility && href.includes("courts")) return <Trophy {...p} />;
    if (href.includes("services")) return <Package {...p} />;
    if (href.includes("programs")) return <ClipboardList {...p} />;
    if (href.includes("market")) return <ShoppingBag {...p} />;
    if (href.includes("odontogram")) return <Stethoscope {...p} />;
    if (href.includes("cobros")) return <Wallet {...p} />;
    return <Settings2 {...p} />;
  }

  const computedNav = mainNav.map((item) => {
    if (item.href === "/dashboard/services" && accountType === "FACILITY") {
      return { ...item, href: "/dashboard/courts", label: "Canchas" };
    }
    return item;
  });

  return (
    <>
      <AgendaSwitcher />
      <nav className={css({ display: "flex", flexDir: "column", gap: "1" })} aria-busy={pendingHref != null}>
        {computedNav.map((item) => {
          const active = isNavActive(pathname, item.href);
          const isPendingNav = pendingHref === item.href;
          const highlighted = active || isPendingNav;

          const linkClass = cx(
            baseLinkClass,
            highlighted && activeLinkClass,
            isPendingNav && !active && css({ opacity: 0.92 }),
          );

          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => {
                if (!active) setPendingHref(item.href);
              }}
              className={linkClass}
            >
              <NavIcon href={item.href} isFacility={accountType === "FACILITY"} />
              {item.label}
            </Link>
          );
        })}

        {/* Cobros — solo visible para cuentas con plan Pro/Enterprise */}
        {(hasProPlan || pathname.startsWith("/dashboard/cobros")) && (
          <Link
            to="/dashboard/cobros"
            onClick={() => { if (!pathname.startsWith("/dashboard/cobros")) setPendingHref("/dashboard/cobros"); }}
            className={cx(
              baseLinkClass,
              pathname.startsWith("/dashboard/cobros") && activeLinkClass,
            )}
          >
            <NavIcon href="/dashboard/cobros" isFacility={false} />
            Cobros
          </Link>
        )}

        {/* Odontograma — solo visible cuando la feature está activa/en prueba */}
        {(hasOdontogram || pathname.startsWith("/dashboard/odontogram")) && (
          <Link
            to="/dashboard/odontogram"
            onClick={() => { if (!pathname.startsWith("/dashboard/odontogram")) setPendingHref("/dashboard/odontogram"); }}
            className={cx(
              baseLinkClass,
              pathname.startsWith("/dashboard/odontogram") && activeLinkClass,
            )}
          >
            <NavIcon href="/dashboard/odontogram" isFacility={false} />
            Odontograma
          </Link>
        )}

        {/* Market — siempre visible */}
        <Link
          to="/dashboard/market"
          onClick={() => { if (pathname !== "/dashboard/market") setPendingHref("/dashboard/market"); }}
          className={cx(
            baseLinkClass,
            pathname === "/dashboard/market" && activeLinkClass,
          )}
        >
          <NavIcon href="/dashboard/market" isFacility={false} />
          Market
          {hasMarketFeatures && pathname !== "/dashboard/market" && (
            <span
              className={css({
                ml: "auto",
                w: "2",
                h: "2",
                rounded: "full",
                bg: "#22c55e",
                flexShrink: 0,
              })}
              aria-label="Tiene features activas"
            />
          )}
        </Link>

        {/* Configuración — siempre al final */}
        <Link
          to={CONFIG_HREF}
          onClick={() => { if (!isConfigArea(pathname)) setPendingHref(CONFIG_HREF); }}
          className={cx(
            baseLinkClass,
            isConfigArea(pathname) && activeLinkClass,
            pendingHref === CONFIG_HREF && !isConfigArea(pathname) && css({ opacity: 0.92 }),
          )}
        >
          <NavIcon href={CONFIG_HREF} isFacility={false} />
          Configuración
        </Link>

        {/* Solicitudes — solo visible cuando hay pendientes o se está en esa ruta */}
        {(requestsCount > 0 || pathname === "/dashboard/requests") && (
          <Link
            to="/dashboard/requests"
            onClick={() => { if (pathname !== "/dashboard/requests") setPendingHref("/dashboard/requests"); }}
            className={cx(
              baseLinkClass,
              (pathname === "/dashboard/requests") && activeLinkClass,
            )}
          >
            <Inbox size={15} aria-hidden />
            Solicitudes
            {requestsCount > 0 && (
              <span
                className={css({
                  ml: "auto",
                  fontSize: "xs",
                  fontWeight: "bold",
                  px: "1.5",
                  py: "0.5",
                  rounded: "full",
                  bg: "#ef4444",
                  color: "white",
                  lineHeight: 1,
                  minW: "4",
                  textAlign: "center",
                })}
              >
                {requestsCount}
              </span>
            )}
          </Link>
        )}
      </nav>
    </>
  );
}
